import {of as observableOf, combineLatest as observableCombineLatest, Observable} from 'rxjs';

import {mergeMap, map} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone} from '@angular/core';
import {AbstractControlComponent} from '../abstract-control';
import {ViewTabComponent} from '../../viewtab.component';
import {ErrorService} from '../../../../error/error.service';
import * as convert from 'xml-js';
import {CacheService} from '../../../../cache/cache.service';
import {LanguageService} from '../../../../language/language.service';
import {ResourceService} from '../../../resource.service';
import {EnvironmentConfigService} from '../../../../core/services/environment-config.service';
import {
  filterAttributesFromDsml,
  getSingleXMLTagValue,
  parseAttributesFromManagementAgent,
} from './SynchronizationFunctions';

@Component({
  selector: 'ctrl-connected-system-scope',
  styleUrls: ['./ctrl-synchronization.scss'],
  template: `
    <table class="table mat-elevation-z8">
      <thead>
        <tr>
          <th>
            <language
              key="CtrlConnectedSystemScopeColumnAttribute"
              defaultValue="Attribute"
            ></language>
          </th>
          <th>
            <language
              key="CtrlConnectedSystemScopeColumnOperator"
              defaultValue="Operator"
            ></language>
          </th>
          <th>
            <language key="CtrlConnectedSystemScopeColumnValue" defaultValue="Value"></language>
          </th>
          <th class="buttons">
            <button
              mat-icon-button
              (click)="addRow()"
              color="accent"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              <mat-icon arial-label="Add">add</mat-icon>
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        @for (entry of formControl?.value; track entry; let i = $index) {
          <tr class="entry">
            <td>
              <mat-form-field>
                <mat-select
                  [(ngModel)]="entry.attribute"
                  (selectionChange)="formControl.updateValueAndValidity()"
                  [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                  [placeholder]="attributePlaceholder | async"
                >
                  @for (attr of attributes | async; track attr) {
                    <mat-option [value]="attr">{{ attr }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </td>
            <td>
              <mat-form-field>
                <mat-select
                  [(ngModel)]="entry.operator"
                  (selectionChange)="formControl.updateValueAndValidity()"
                  [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                  [placeholder]="operatorPlaceholder | async"
                >
                  <mat-option value="EQUAL">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorEqual"
                      defaultValue="equal"
                    ></language>
                  </mat-option>
                  <mat-option value="NOTEQUAL">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorNotEqual"
                      defaultValue="not equal"
                    ></language>
                  </mat-option>
                  <mat-option value="LESSTHAN">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorLessThan"
                      defaultValue="less than"
                    ></language>
                  </mat-option>
                  <mat-option value="LESSTHAN_OR_EQUAL">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorLessOrEqual"
                      defaultValue="less or equal"
                    ></language>
                  </mat-option>
                  <mat-option value="CONTAINS">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorContains"
                      defaultValue="contains"
                    ></language>
                  </mat-option>
                  <mat-option value="NOTCONTAINS">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorNotContains"
                      defaultValue="not contains"
                    ></language>
                  </mat-option>
                  <mat-option value="STARTSWITH">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorStartsWith"
                      defaultValue="starts with"
                    ></language>
                  </mat-option>
                  <mat-option value="NOTSTARTSWITH">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorNotStartsWith"
                      defaultValue="not starts with"
                    ></language>
                  </mat-option>
                  <mat-option value="ENDSWITH">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorEndsWith"
                      defaultValue="ends with"
                    ></language>
                  </mat-option>
                  <mat-option value="NOTENDSWITH">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorNotEndsWith"
                      defaultValue="not ends with"
                    ></language>
                  </mat-option>
                  <mat-option value="GREATERTHAN">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorGreaterThan"
                      defaultValue="greater than"
                    ></language>
                  </mat-option>
                  <mat-option value="GREATERTHAN_OR_EQUAL">
                    <language
                      key="CtrlConnectedSystemScopeColumnAttributeOperatorGreaterOrEqual"
                      defaultValue="greater or equal"
                    ></language>
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </td>
            <td>
              <mat-form-field>
                <input
                  matInput
                  [(ngModel)]="entry.value"
                  [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                  (change)="formControl.updateValueAndValidity()"
                />
              </mat-form-field>
            </td>
            <td class="buttons">
              <button
                mat-icon-button
                (click)="delete(i)"
                [disabled]="(getCaughtObservable(isEnabled) | async) != true"
              >
                <mat-icon aria-label="Delete">close</mat-icon>
              </button>
            </td>
          </tr>
        }
      </tbody>
    </table>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlConnectedSystemScopeComponent extends AbstractControlComponent {
  constructor(
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    private cacheService: CacheService,
    private resourceService: ResourceService,
    private envConfigService: EnvironmentConfigService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, _ngZone, errorService);
    this.attributePlaceholder = this.cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'CtrlConnectedSystemScopeColumnAttributeAttributePlaceholder',
          'Please select an attribute.',
          lr,
        );
      }),
    );
    this.operatorPlaceholder = this.cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'CtrlConnectedSystemScopeColumnAttributeOperatorPlaceholder',
          'Please select an item.',
          lr,
        );
      }),
    );
  }

  attributePlaceholder: Observable<string>;
  operatorPlaceholder: Observable<string>;
  attributes: Observable<string[]>;

  protected formatSource(source: any): any {
    // <scoping><scope><csAttribute>displayName</csAttribute><csOperator>EQUAL</csOperator><csValue>x</csValue></scope></scoping>
    if (source == null || typeof source !== 'string' || source.length === 0) {
      return [];
    }

    let result: ConnectedSystemScope[] = [];

    const obj: any = convert.xml2js(source, {
      compact: true,
      alwaysArray: true,
    });

    if (obj == null || !Array.isArray(obj.scoping) || obj.scoping.length !== 1) {
      throw new Error('Failed converting UocConnectedSystemScope Source XML to JS');
    }

    if (Array.isArray(obj.scoping[0].scope)) {
      obj.scoping[0].scope.forEach(xmlScope => {
        const scope = new ConnectedSystemScope();
        scope.attribute = getSingleXMLTagValue(xmlScope, 'csAttribute');
        scope.operator = getSingleXMLTagValue(xmlScope, 'csOperator');
        scope.value = getSingleXMLTagValue(xmlScope, 'csValue');
        result.push(scope);
      });
    }

    if (result.length === 0) {
      result.push(new ConnectedSystemScope());
    }

    return result;
  }

  protected formatTarget(value: any): any {
    if (!Array.isArray(value)) {
      return null; // TODO: is the empty value null or []?
    }

    value = value.filter(
      (val: ConnectedSystemScope) => val.attribute != null && val.operator != null,
    );
    if (value.length === 0) {
      return null; // TODO: is the empty value null or []?
    }

    const obj = {
      scoping: {
        scope: [],
      },
    };

    value.forEach((val: ConnectedSystemScope) => {
      if (!(val instanceof ConnectedSystemScope)) {
        return;
      }

      const scope = {
        csAttribute: undefined,
        csOperator: undefined,
        csValue: undefined,
      };

      if (val.attribute != null) {
        scope.csAttribute = {_text: val.attribute};
      }

      if (val.operator != null) {
        scope.csOperator = {_text: val.operator};
      }

      if (val.value != null) {
        scope.csValue = {_text: val.value};
      }

      obj.scoping.scope.push(scope);
    });

    const xml = convert.js2xml(obj, {
      compact: true,
    });

    return xml;
  }

  protected initValidators(): void {
    this.attributes = this._config.getPropertyValues('Mode').pipe(
      mergeMap(mode => {
        if (mode === 'Outbound') {
          return this._config
            .getPropertyValues('ManagementAgent')
            .pipe(
              mergeMap(id =>
                parseAttributesFromManagementAgent(id, this.resourceService, this.envConfigService),
              ),
            );
        } else if (mode === 'Inbound') {
          return observableCombineLatest(
            this._config.getPropertyValues('MVSchema'),
            this._config.getPropertyValues('MVObjectType'),
            filterAttributesFromDsml,
          );
        } else {
          return observableOf([]);
        }
      }),
    );
  }

  delete(index: number): void {
    this.formControl.value.splice(index, 1);
    this.formControl.updateValueAndValidity();
  }

  addRow(): void {
    // don't fire event - unnecessary
    if (!Array.isArray(this.formControl.value)) {
      this.formControl.setValue([new ConnectedSystemScope()], {emitEvent: false});
    } else {
      this.formControl.value.push(new ConnectedSystemScope());
    }
  }
}

class ConnectedSystemScope {
  attribute: string;
  operator:
    | null
    | 'EQUAL'
    | 'NOTEQUAL'
    | 'LESSTHAN'
    | 'LESSTHAN_OR_EQUAL'
    | 'CONTAINS'
    | 'NOTCONTAINS'
    | 'STARTSWITH'
    | 'NOTSTARTSWITH'
    | 'ENDSWITH'
    | 'NOTENDSWITH'
    | 'GREATERTHAN'
    | 'GREATERTHAN_OR_EQUAL';
  value: string;
}
